import {
  ColumnMapping,
  ExcelProcessorConfig,
  HeaderConfig,
  ParsedExcelData,
  parseNumber,
  ValidationError
} from '@/lib/excel';
import { roundNumber } from '@/lib/formatUtils';

const HEADERS = {
  MA_VT: 'Mã vật tư*',
  TEN_VT: 'Tên vật tư',
  DVT: 'Đvt*',
  HE_SO: 'Hệ số*'
} as const;

const HEADER_CONFIG: HeaderConfig[] = [
  { header: HEADERS.MA_VT, key: 'ma_vt', type: 'main' },
  { header: HEADERS.TEN_VT, key: 'ten_vt', type: 'main' },
  { header: HEADERS.DVT, key: 'dvt', type: 'main' },
  { header: HEADERS.HE_SO, key: 'he_so', type: 'main' }
];

const UUID_FIELDS = ['ma_vt', 'dvt'];
type ExcelSection = Record<string, unknown[]>;
type UUIDLookupResponse = Partial<Record<'main', ExcelSection>>;

const getFieldName = (key: string) => {
  const config = HEADER_CONFIG.find(c => c.key === key);
  return config?.header?.replace('*', '').trim() || key;
};

const UUIDPayload = (parsedData: ParsedExcelData) => {
  const mainData = parsedData.main || {};

  const pick = (obj: ExcelSection, keys: string[]) =>
    keys.reduce((acc, key) => {
      if (key in obj) {
        acc[key] = obj[key];
      }
      return acc;
    }, {} as ExcelSection);

  return {
    main: pick(mainData, UUID_FIELDS)
  };
};

const ValidateUUID = (
  response: UUIDLookupResponse | null | undefined,
  columnMapping: Record<string, ColumnMapping>
): ValidationError[] => {
  if (!response) return [];
  const errors: ValidationError[] = [];

  (['main'] as const).forEach(section => {
    const data = response[section];
    if (!data) return;

    Object.entries(data).forEach(([field, values]) => {
      if (!Array.isArray(values)) return;
      if (!UUID_FIELDS.includes(field)) return;
      const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === section);

      values.forEach((val, i) => {
        if (val === null) {
          errors.push({
            row: i + 2,
            field,
            message: `${getFieldName(field)} không hợp lệ`,
            section,
            columnIndex: mapping?.index
          });
        }
      });
    });
  });

  return errors;
};

const TransformToBodies = (
  dataRows: unknown[][],
  parsedData: ParsedExcelData,
  UUIDResponse: UUIDLookupResponse | null | undefined,
  _context: { entityUnitUUID: string }
): ImportBody[] => {
  const mainData = parsedData.main || {};
  const rowCount = (mainData.ma_vt || []).length;

  const bodies: ImportBody[] = [];

  for (let i = 0; i < rowCount; i++) {
    const ma_vt = ((UUIDResponse?.main?.ma_vt && UUIDResponse.main.ma_vt[i]) || '') as string;
    const dvt = ((UUIDResponse?.main?.dvt && UUIDResponse.main.dvt[i]) || '') as string;
    const he_so = roundNumber(mainData.he_so?.[i] || 0, 6);

    if (!ma_vt && !dvt && !he_so) continue;

    bodies.push({
      ma_vt,
      dvt,
      he_so,
      status: '1'
    });
  }

  return bodies;
};

export interface ImportBody {
  ma_vt: string;
  dvt: string;
  he_so: number;
  status: string;
}

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};
